import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Building2,
  ArrowRight,
  ArrowLeft,
  Loader2,
  Info
} from 'lucide-react';
import toast from 'react-hot-toast';
import onboardingService, { BusinessProfile } from '../../services/onboardingService';

interface BriefingStepProps {
  profile: BusinessProfile | null;
  updateProfile: (updates: Partial<BusinessProfile>) => void;
  onNext: () => void;
  onBack: () => void;
}

const BriefingStep: React.FC<BriefingStepProps> = ({
  profile,
  updateProfile,
  onNext,
  onBack
}) => {
  const { t } = useTranslation();

  const INDUSTRIES = [
    { value: 'technology', label: t('onboarding.briefing.industries.technology', 'Technology') },
    { value: 'ecommerce', label: t('onboarding.briefing.industries.ecommerce', 'E-commerce') },
    { value: 'health', label: t('onboarding.briefing.industries.health', 'Health') },
    { value: 'education', label: t('onboarding.briefing.industries.education', 'Education') },
    { value: 'finance', label: t('onboarding.briefing.industries.finance', 'Finance') },
    { value: 'food', label: t('onboarding.briefing.industries.food', 'Food') },
    { value: 'fashion', label: t('onboarding.briefing.industries.fashion', 'Fashion') },
    { value: 'beauty', label: t('onboarding.briefing.industries.beauty', 'Beauty') },
    { value: 'realestate', label: t('onboarding.briefing.industries.realestate', 'Real Estate') },
    { value: 'automotive', label: t('onboarding.briefing.industries.automotive', 'Automotive') },
    { value: 'travel', label: t('onboarding.briefing.industries.travel', 'Travel') },
    { value: 'services', label: t('onboarding.briefing.industries.services', 'Services') },
    { value: 'consulting', label: t('onboarding.briefing.industries.consulting', 'Consulting') },
    { value: 'marketing', label: t('onboarding.briefing.industries.marketing', 'Marketing') },
    { value: 'retail', label: t('onboarding.briefing.industries.retail', 'Retail') },
    { value: 'manufacturing', label: t('onboarding.briefing.industries.manufacturing', 'Manufacturing') },
    { value: 'other', label: t('onboarding.briefing.industries.other', 'Other') }
  ];

  const BRAND_VOICES = [
    { value: 'professional', label: t('onboarding.briefing.voices.professional', 'Professional'), description: t('onboarding.briefing.voices.professionalDesc', 'Serious and reliable') },
    { value: 'casual', label: t('onboarding.briefing.voices.casual', 'Casual'), description: t('onboarding.briefing.voices.casualDesc', 'Relaxed and accessible') },
    { value: 'friendly', label: t('onboarding.briefing.voices.friendly', 'Friendly'), description: t('onboarding.briefing.voices.friendlyDesc', 'Welcoming and approachable') },
    { value: 'formal', label: t('onboarding.briefing.voices.formal', 'Formal'), description: t('onboarding.briefing.voices.formalDesc', 'Traditional and respectful') },
    { value: 'bold', label: t('onboarding.briefing.voices.bold', 'Bold'), description: t('onboarding.briefing.voices.boldDesc', 'Direct and impactful') },
    { value: 'inspirational', label: t('onboarding.briefing.voices.inspirational', 'Inspirational'), description: t('onboarding.briefing.voices.inspirationalDesc', 'Motivational and positive') },
    { value: 'educational', label: t('onboarding.briefing.voices.educational', 'Educational'), description: t('onboarding.briefing.voices.educationalDesc', 'Informative and instructive') }
  ];

  const [formData, setFormData] = useState({
    businessName: profile?.business_name || '',
    businessDescription: profile?.business_description || '',
    industry: profile?.industry || '',
    brandVoice: profile?.brand_voice || 'professional'
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (profile) {
      setFormData({
        businessName: profile.business_name || '',
        businessDescription: profile.business_description || '',
        industry: profile.industry || '',
        brandVoice: profile.brand_voice || 'professional'
      });
    }
  }, [profile]);

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.businessDescription.trim()) {
      toast.error(t('onboarding.briefing.describeError', 'Please describe your business'));
      return;
    }

    setIsSaving(true);
    try {
      const updatedProfile = await onboardingService.saveBriefing(formData);
      updateProfile(updatedProfile);
      toast.success(t('onboarding.briefing.saved', 'Information saved!'));
      onNext();
    } catch (error) {
      console.error('Error saving briefing:', error);
      toast.error(t('onboarding.briefing.saveError', 'Error saving. Please try again.'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-5 sm:p-12">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 mb-4 shadow-lg">
          <Building2 className="h-8 w-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          {t('onboarding.briefing.title', 'Tell us about your business')}
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          {t('onboarding.briefing.subtitle', 'The more details, the better the AI-generated content')}
        </p>
      </div>

      {/* Form */}
      <div className="max-w-xl mx-auto space-y-6">
        {/* Business Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('onboarding.briefing.businessName', 'Business name')}
          </label>
          <input
            type="text"
            value={formData.businessName}
            onChange={(e) => handleChange('businessName', e.target.value)}
            placeholder={t('onboarding.briefing.businessNamePlaceholder', 'E.g.: My Company LLC')}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
          />
        </div>

        {/* Industry */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('onboarding.briefing.industry', 'Industry')}
          </label>
          <select
            value={formData.industry}
            onChange={(e) => handleChange('industry', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
          >
            <option value="">{t('onboarding.briefing.selectIndustry', 'Select industry')}</option>
            {INDUSTRIES.map((industry) => (
              <option key={industry.value} value={industry.value}>
                {industry.label}
              </option>
            ))}
          </select>
        </div>

        {/* Business Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('onboarding.briefing.description', 'Describe your business')} <span className="text-red-500">*</span>
          </label>
          <textarea
            value={formData.businessDescription}
            onChange={(e) => handleChange('businessDescription', e.target.value)}
            placeholder={t('onboarding.briefing.descriptionPlaceholder', 'E.g.: We are a technology company that develops marketing automation solutions for small and medium businesses. Our differentiator is ease of use and personalized support...')}
            rows={5}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all resize-none"
          />
          <div className="mt-2 flex items-start space-x-2 text-xs text-gray-500 dark:text-gray-400">
            <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>
              {t('onboarding.briefing.descriptionHint', 'Include: what you sell/offer, who is your ideal customer, and what makes your business unique.')}
            </span>
          </div>
        </div>

        {/* Brand Voice */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            {t('onboarding.briefing.brandVoice', 'Brand voice')}
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {BRAND_VOICES.map((voice) => (
              <button
                key={voice.value}
                type="button"
                onClick={() => handleChange('brandVoice', voice.value)}
                className={`
                  p-3 rounded-xl border-2 text-left transition-all
                  ${formData.brandVoice === voice.value
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                  }
                `}
              >
                <div className={`font-medium ${formData.brandVoice === voice.value ? 'text-primary-700 dark:text-primary-300' : 'text-gray-900 dark:text-white'}`}>
                  {voice.label}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {voice.description}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center mt-10 pt-6 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={onBack}
          className="flex items-center px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('common.back', 'Back')}
        </button>

        <button
          onClick={handleSave}
          disabled={isSaving || !formData.businessDescription.trim()}
          className="flex items-center px-6 py-3 bg-gradient-to-r from-primary-600 to-purple-600 text-white rounded-xl font-semibold shadow-lg shadow-primary-500/30 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {isSaving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              {t('common.saving', 'Saving...')}
            </>
          ) : (
            <>
              {t('common.next', 'Continue')}
              <ArrowRight className="h-4 w-4 ml-2" />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default BriefingStep;
